/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { ErrorInfo, ReactNode } from 'react';

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<Props, State> {
  state: State = {
    hasError: false,
    error: null
  };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-screen bg-gray-900 text-white p-4">
          <h1 className="text-2xl font-bold mb-4 text-red-500">Something went wrong</h1>
          <pre className="bg-gray-800 p-4 rounded max-w-full overflow-auto text-sm mb-4">
            {this.state.error?.message}
          </pre>
          <button
            className="px-4 py-2 bg-indigo-600 rounded hover:bg-indigo-700"
            onClick={() => window.location.reload()}
          >
            Reload App
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;